/* eslint-disable react/no-unescaped-entities, react/require-default-props */
import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useIntl } from 'react-intl';
import { Button, FlexColumn, FlexRow, Textarea, Typography } from 'design-system';
import { CheckIcon, EditIcon } from 'design-system/components/Icons';
import { toDisplayableMediaUrl } from '../helpers/invitation-preview-helpers';
import LeadershipMessageContent from './LeadershipMessageContent';

function initialsFromName(name = '') {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

function LeadershipAvatar({ image, name, className = '' }) {
  const fallback = useMemo(() => initialsFromName(name), [name]);
  const [imageFailed, setImageFailed] = useState(false);
  const resolvedImage = useMemo(() => toDisplayableMediaUrl(image), [image]);
  const showImage = Boolean(resolvedImage) && !imageFailed;

  return (
    <FlexRow
      align="center"
      justify="center"
      inline
      fullWidth={false}
      className={['cc-preview-leadership__avatar', className].filter(Boolean).join(' ')}
      aria-hidden
    >
      {showImage ? (
        <img
          src={resolvedImage}
          alt=""
          className="cc-preview-leadership__avatar-image"
          onError={() => setImageFailed(true)}
        />
      ) : (
        <Typography component="span" className="cc-preview-leadership__avatar-initials">
          {fallback || '?'}
        </Typography>
      )}
    </FlexRow>
  );
}

LeadershipAvatar.propTypes = {
  image: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
};

export default function LeadershipPreview({
  leadershipUser,
  recipient,
  reply = '',
  onReplyChange,
  replySaved = false,
  onReplySavedChange,
}) {
  const intl = useIntl();

  const senderLine = useMemo(() => {
    return [leadershipUser?.name, leadershipUser?.companyName].filter(Boolean).join(' · ');
  }, [leadershipUser?.name, leadershipUser?.companyName]);

  const recipientName = useMemo(() => {
    const raw = (recipient || '').trim();
    if (!raw) {
      return '';
    }
    return raw.split(',')[0]?.trim() || raw;
  }, [recipient]);

  const handleSaveMessage = () => {
    const trimmed = reply.trim();
    if (!trimmed) {
      return;
    }
    onReplySavedChange?.(true);
  };

  const handleEdit = () => {
    onReplySavedChange?.(false);
  };

  return (
    <FlexColumn align="stretch" fullWidth className="cc-preview-leadership" gap={18}>
      <FlexRow align="start" fullWidth className="cc-preview-leadership__incoming-row" gap={16}>
        <LeadershipAvatar
          image={leadershipUser?.image}
          name={leadershipUser?.name}
          className="cc-preview-leadership__avatar--leader"
        />
        <FlexColumn
          align="stretch"
          fullWidth
          className="cc-preview-leadership__bubble cc-preview-leadership__bubble--incoming"
        >
          {senderLine ? (
            <Typography component="p" className="cc-preview-leadership__sender">
              {senderLine}
            </Typography>
          ) : null}
          {leadershipUser?.email ? (
            <Typography component="a" href={`mailto:${leadershipUser.email}`} className="cc-preview-leadership__email">
              {leadershipUser.email}
            </Typography>
          ) : null}
          <LeadershipMessageContent
            leadershipUser={leadershipUser}
            component="div"
            className="cc-preview-leadership__quote"
          />
        </FlexColumn>
      </FlexRow>

      {replySaved ? (
        <FlexRow align="start" fullWidth className="cc-preview-leadership__reply-row" gap={16}>
          <FlexColumn
            align="stretch"
            fullWidth
            className="cc-preview-leadership__bubble cc-preview-leadership__bubble--reply"
          >
            <FlexRow align="center" justify="between" fullWidth className="cc-preview-leadership__reply-header">
              <FlexRow align="center" fullWidth={false} className="cc-preview-leadership__saved-meta" gap={7}>
                <Typography component="span" className="cc-preview-leadership__reply-label">
                  {intl.formatMessage({ id: 'client-connect-preview-your-reply' })}
                </Typography>
                <FlexRow align="center" fullWidth={false} className="cc-preview-leadership__saved-badge" gap={4}>
                  <CheckIcon size={12} aria-hidden className="cc-preview-leadership__saved-icon" />
                  <Typography component="span" className="cc-preview-leadership__saved-text">
                    {intl.formatMessage({ id: 'client-connect-preview-saved' })}
                  </Typography>
                </FlexRow>
              </FlexRow>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                className="cc-preview-leadership__edit-btn"
                icon={<EditIcon size={14} fill="currentColor" aria-hidden />}
                onClick={handleEdit}
              >
                {intl.formatMessage({ id: 'client-connect-preview-edit' })}
              </Button>
            </FlexRow>
            <Typography component="p" className="cc-preview-leadership__reply-text">
              {reply}
            </Typography>
          </FlexColumn>
          <LeadershipAvatar
            name={recipientName}
            className="cc-preview-leadership__avatar--recipient cc-preview-leadership__avatar--recipient-gradient"
          />
        </FlexRow>
      ) : (
        <FlexColumn align="stretch" fullWidth className="cc-preview-leadership__compose" gap={0}>
          <Textarea
            rows={4}
            value={reply}
            className="cc-preview-leadership__textarea"
            placeholder={intl.formatMessage({
              id: 'client-connect-preview-leadership-placeholder',
            })}
            onChange={(event) => {
              onReplyChange?.(event.target.value);
            }}
          />
          <FlexRow align="center" justify="between" fullWidth className="cc-preview-leadership__compose-footer">
            <Typography component="p" className="cc-preview-leadership-footer__hint">
              {intl.formatMessage({ id: 'client-connect-preview-message-footer' })}
            </Typography>
            <Button
              type="button"
              variant="primary"
              size="base"
              className="cc-preview-leadership__save-btn"
              icon={<CheckIcon size={16} aria-hidden />}
              disabled={!reply.trim()}
              onClick={handleSaveMessage}
            >
              {intl.formatMessage({ id: 'client-connect-preview-save-message' })}
            </Button>
          </FlexRow>
        </FlexColumn>
      )}
    </FlexColumn>
  );
}

LeadershipPreview.propTypes = {
  onReplyChange: PropTypes.func,
  onReplySavedChange: PropTypes.func,
  recipient: PropTypes.string,
  reply: PropTypes.string,
  replySaved: PropTypes.bool,
  leadershipUser: PropTypes.shape({
    name: PropTypes.string,
    image: PropTypes.string,
    email: PropTypes.string,
    companyName: PropTypes.string,
    message: PropTypes.string,
  }),
};
